import React, { useState } from "react";
import { Download, FileText, Loader2, CheckCircle2, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Icon } from "@/components/Sections";
import { API, useLeadForm } from "@/hooks/useLeadForm";

const FIELD = "w-full rounded-xl border border-navy/10 bg-lightbg px-4 py-3 text-[15px] text-navy placeholder:text-ink/40 outline-none transition-colors focus:border-royal focus:ring-2 focus:ring-royal/20";

const Err = ({ msg }) => (msg ? <p className="mt-1 text-xs text-red-500">{msg}</p> : null);

const UnlockSuccess = ({ guide, leadId }) => (
  <div data-testid="guide-unlocked" className="flex flex-col items-center py-4 text-center">
    <span className="flex h-14 w-14 items-center justify-center rounded-full bg-gold/15 text-gold"><CheckCircle2 className="h-8 w-8" /></span>
    <h3 className="mt-4 font-display text-xl font-bold text-navy">Your checklist is ready</h3>
    <p className="mt-2 text-sm text-ink/60">We've also noted your details — our team may reach out to help you apply it.</p>
    <a
      data-testid="guide-download-link"
      href={`${API}/guides/${guide.slug}.pdf?lead=${leadId}`}
      className="mt-6 inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#0d2c56]"
    >
      <Download className="h-4 w-4" /> Download PDF
    </a>
  </div>
);

const UnlockForm = ({ guide }) => {
  const { form, set, consent, setConsent, errors, status, leadId, submit } = useLeadForm(
    { name: "", mobile: "", email: "" },
    (f) => ({ ...f, primary_goal: guide.title, source: `guide:${guide.slug}` }),
  );

  if (status === "success") return <UnlockSuccess guide={guide} leadId={leadId} />;

  return (
    <form onSubmit={submit} data-testid="guide-form" className="space-y-3">
      <div>
        <input data-testid="guide-name" className={FIELD} placeholder="Your name" value={form.name} onChange={(e) => set("name", e.target.value)} />
        <Err msg={errors.name} />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <input data-testid="guide-mobile" className={FIELD} placeholder="Mobile number" inputMode="tel" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} />
          <Err msg={errors.mobile} />
        </div>
        <div>
          <input data-testid="guide-email" className={FIELD} placeholder="Email" inputMode="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
          <Err msg={errors.email} />
        </div>
      </div>
      <label className="flex cursor-pointer items-start gap-3 text-xs text-ink/60">
        <input type="checkbox" data-testid="guide-consent" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 h-4 w-4 accent-royal" />
        <span>I agree to be contacted by Auxieron and accept the <a href="/privacy" className="font-semibold text-royal underline">Privacy Policy</a>.</span>
      </label>
      <Err msg={errors.consent} />
      {status === "error" && <p data-testid="guide-error" className="text-xs text-red-500">Something went wrong. Please try again.</p>}
      <button type="submit" data-testid="guide-submit" disabled={status === "loading"} className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-3.5 text-sm font-bold text-navy shadow-gold transition-transform hover:-translate-y-0.5 disabled:opacity-70">
        {status === "loading" ? <><Loader2 className="h-4 w-4 animate-spin" /> Unlocking...</> : <>Unlock free checklist <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></>}
      </button>
    </form>
  );
};

const CardBody = ({ guide, compact }) => (
  <div className={compact ? "p-6" : "flex flex-1 flex-col p-6"}>
    {compact && (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-gold/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-navy"><FileText className="h-3 w-3" /> Free checklist</span>
    )}
    <div className={`flex items-center gap-3 ${compact ? "mt-4" : ""}`}>
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-navy text-gold"><Icon name={guide.icon} className="h-5 w-5" /></span>
      <h3 className="font-display text-base font-bold leading-snug text-navy group-hover:text-royal sm:text-lg">{guide.title}</h3>
    </div>
    <p className="mt-3 text-sm leading-relaxed text-ink/60">{guide.line}</p>
    <span className={`inline-flex items-center gap-1.5 text-sm font-semibold text-royal ${compact ? "mt-4" : "mt-auto pt-5"}`}>
      <Download className="h-4 w-4" /> Get the checklist
    </span>
  </div>
);

// Card + modal. `compact` renders the sidebar version.
export const GuideCard = ({ guide, compact = false }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        data-testid={`guide-card-${guide.slug}`}
        onClick={() => setOpen(true)}
        className={`group w-full overflow-hidden rounded-2xl border border-navy/[0.08] bg-white text-left transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-hover ${compact ? "" : "flex h-full flex-col"}`}
      >
        {!compact && (
          <div className="relative aspect-[16/9] overflow-hidden">
            <img src={guide.cover} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <span className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-navy backdrop-blur"><FileText className="h-3 w-3" /> Free PDF</span>
          </div>
        )}
        <CardBody guide={guide} compact={compact} />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="guide-dialog" className="max-w-md rounded-3xl border-navy/10 bg-white p-7 sm:rounded-3xl">
          <DialogTitle className="font-display text-xl font-bold text-navy">{guide.title}</DialogTitle>
          <DialogDescription className="text-sm text-ink/60">
            Enter your details to unlock the PDF. No spam — we'll only reach out about your financial goals.
          </DialogDescription>
          <UnlockForm guide={guide} />
        </DialogContent>
      </Dialog>
    </>
  );
};
